import { MARKET_PAIRS } from '../data/mockMarket';
import type { PairMetric } from './usePairMetrics';
import { usePairStats } from './useSubgraph';

type VolumeSource = 'subgraph' | 'local' | 'none';

export interface ProtocolStats {
  tvlUsd: number | undefined;
  volume24hUsd: number | undefined;
  fees24hUsd: number | undefined;
  activePairs: number;
  totalPairs: number;
  volumeSource: VolumeSource;
  loading: boolean;
}

export function aggregateProtocolStats(args: {
  metrics: Pick<PairMetric, 'tvlUsd' | 'volume24hUsd'>[];
  subgraphDailyVolumeUsd?: number;
}): {
  tvlUsd: number | undefined;
  volume24hUsd: number | undefined;
  fees24hUsd: number | undefined;
  activePairs: number;
  volumeSource: 'subgraph' | 'local' | 'none';
} {
  const definedTvl = args.metrics.filter((metric) => metric.tvlUsd !== undefined);
  const tvlUsd = definedTvl.length === 0 ? undefined : definedTvl.reduce((sum, metric) => sum + metric.tvlUsd!, 0);
  const activePairs = args.metrics.filter((metric) => metric.tvlUsd !== undefined && metric.tvlUsd > 0).length;
  const localVolumeUsd = args.metrics.reduce((sum, metric) => sum + metric.volume24hUsd, 0);

  let volume24hUsd: number | undefined;
  let volumeSource: VolumeSource;

  if (args.subgraphDailyVolumeUsd !== undefined) {
    volume24hUsd = args.subgraphDailyVolumeUsd;
    volumeSource = 'subgraph';
  } else if (localVolumeUsd > 0) {
    volume24hUsd = localVolumeUsd;
    volumeSource = 'local';
  } else {
    volume24hUsd = undefined;
    volumeSource = 'none';
  }

  return {
    tvlUsd,
    volume24hUsd,
    fees24hUsd: volume24hUsd === undefined ? undefined : volume24hUsd * 0.003,
    activePairs,
    volumeSource,
  };
}

// Takes the already-fetched metrics (single source of truth in the parent) so the
// dashboard subscribes to on-chain pair data exactly once instead of per-consumer.
export function useProtocolStats(metrics: PairMetric[], loading: boolean): ProtocolStats {
  const { data, configured } = usePairStats();

  const cutoff = Date.now() - 86_400_000;
  const subgraphDailyVolumeUsd =
    configured && data
      ? data.pairDayDatas
          .filter((dayData) => dayData.date * 1000 >= cutoff)
          .reduce((sum, dayData) => sum + Number(dayData.dailyVolumeUSD), 0)
      : undefined;

  return {
    ...aggregateProtocolStats({ metrics, subgraphDailyVolumeUsd }),
    totalPairs: MARKET_PAIRS.length,
    loading,
  };
}
